import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Rating,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  Paper,
  Divider,
  IconButton,
  InputAdornment,
  Slider,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ShoppingCart as CartIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { products, categories, searchProducts } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import { Link as RouterLink } from 'react-router-dom';

const Products: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLanguage();
  const { addItem, isInCart } = useCart();
  const { toggleFavorite, isFavorite } = useFavorites();
  const [searchParams, setSearchParams] = useSearchParams();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // State
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'name');
  const [priceRange, setPriceRange] = useState<number[]>([0, 500000]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(!isMobile);
  
  const itemsPerPage = 12;

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = products;

    // Search filter
    if (searchQuery) {
      filtered = searchProducts(searchQuery);
    }

    // Category filter
    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Price range filter
    filtered = filtered.filter(product => 
      product.price >= priceRange[0] && product.price <= priceRange[1]
    );

    // Sort products
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'rating':
          return b.rating - a.rating;
        case 'newest':
          return (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0);
        case 'name':
        default:
          return a.nameFr.localeCompare(b.nameFr);
      }
    });

    return filtered;
  }, [searchQuery, selectedCategory, sortBy, priceRange]);

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Update URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy !== 'name') params.set('sort', sortBy);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, sortBy, setSearchParams]);

  // Handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  // Handle category change
  const handleCategoryChange = (event: any) => {
    setSelectedCategory(event.target.value);
    setPage(1);
  };

  // Handle sort change
  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
    setPage(1);
  };

  // Handle price range change
  const handlePriceRangeChange = (event: Event, newValue: number | number[]) => {
    setPriceRange(newValue as number[]);
    setPage(1);
  };

  // Handle pagination change with scroll to top
  const handlePageChange = (event: any, value: number) => {
    setPage(value);
    // Scroll to top of the page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle add to cart
  const handleAddToCart = (product: any) => {
    addItem({
      id: product.id,
      name: product.nameFr,
      price: product.price,
      image: product.image,
      stock: product.stock,
    });
    setSnackbarMessage(`${product.nameFr} ajouté au panier`);
    setSnackbarOpen(true);
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString() + ' FCFA';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Grid container spacing={3}>
        {/* Header and Search and Filters Bar - Mobile Collapsible */}
        <Grid item xs={12} md={3}>
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3,
              backgroundColor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              boxShadow: 2,
              height: 'fit-content',
              display: { xs: showFilters ? 'block' : 'none', md: 'block' }
            }}
          >
            {/* Header */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {t.products.title}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {filteredProducts.length} {t.products.resultsFound}
              </Typography>
            </Box>

            <Stack spacing={2}>
              {/* Search */}
              <TextField
                fullWidth
                placeholder={t.products.searchPlaceholder}
                value={searchQuery}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {/* Category Filter */}
              <FormControl fullWidth>
                <InputLabel>{t.products.category}</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label={t.products.category}
                >
                  <MenuItem value="">{t.products.allCategories}</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.nameFr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Sort */}
              <FormControl fullWidth>
                <InputLabel>{t.products.sortBy}</InputLabel>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  label={t.products.sortBy}
                >
                  <MenuItem value="name">{t.products.sortName}</MenuItem>
                  <MenuItem value="price-low">{t.products.sortPriceLow}</MenuItem>
                  <MenuItem value="price-high">{t.products.sortPriceHigh}</MenuItem>
                  <MenuItem value="rating">{t.products.sortRating}</MenuItem>
                  <MenuItem value="newest">{t.products.sortNewest}</MenuItem>
                </Select>
              </FormControl>

              {/* View Mode and Filter Toggle */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <IconButton
                  onClick={() => setViewMode('grid')}
                  color={viewMode === 'grid' ? 'primary' : 'default'}
                >
                  <ViewModuleIcon />
                </IconButton>
                <IconButton
                  onClick={() => setViewMode('list')}
                  color={viewMode === 'list' ? 'primary' : 'default'}
                >
                  <ViewListIcon />
                </IconButton>
                {isMobile && (
                  <IconButton
                    onClick={() => setShowFilters(!showFilters)}
                    color="primary"
                  >
                    <FilterIcon />
                  </IconButton>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* Filters Sidebar */}
          {showFilters && (
            <Paper sx={{ 
              p: 2,
              display: { xs: 'block', md: 'block' }
            }}>
              <Typography variant="h6" gutterBottom>
                {t.products.filters}
              </Typography>

              {/* Price Range */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t.products.priceRange}
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={handlePriceRangeChange}
                  valueLabelDisplay="auto"
                  min={0}
                  max={500000}
                  step={1000}
                  valueLabelFormat={(value) => formatPrice(value)}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="body2">{formatPrice(priceRange[0])}</Typography>
                  <Typography variant="body2">{formatPrice(priceRange[1])}</Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Categories */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  {t.products.categories}
                </Typography>
                {categories.map((category) => (
                  <FormControlLabel
                    key={category.id}
                    control={
                      <Checkbox
                        checked={selectedCategory === category.id}
                        onChange={() => setSelectedCategory(
                          selectedCategory === category.id ? '' : category.id
                        )}
                        size="small"
                      />
                    }
                    label={category.nameFr}
                    sx={{ display: 'block', mb: 1 }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Stock Status */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t.products.stockStatus}
                </Typography>
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label={t.products.inStock}
                />
                <FormControlLabel
                  control={<Checkbox size="small" />}
                  label={t.products.lowStock}
                />
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Products Grid */}
        <Grid item xs={12} md={9}>
          {/* Mobile Filter Toggle Button */}
          {isMobile && (
            <Box sx={{ mb: 2, display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ mb: 2 }}
              >
                {showFilters ? 'Masquer les filtres' : 'Afficher les filtres'}
              </Button>
            </Box>
          )}

          {/* Products Grid/List */}
          <Grid container spacing={2}>
            {paginatedProducts.map((product) => (
              <Grid 
                item 
                xs={12} 
                sm={viewMode === 'grid' ? 6 : 12} 
                lg={viewMode === 'grid' ? 4 : 12} 
                xl={viewMode === 'grid' ? 3 : 12} 
                key={product.id}
              >
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: viewMode === 'list' ? 'row' : 'column',
                  overflow: 'hidden'
                }}>
                  <CardMedia
                    component="img"
                    sx={{ 
                      width: viewMode === 'list' ? 200 : '100%',
                      height: viewMode === 'list' ? 200 : 200,
                      objectFit: 'cover',
                      flexShrink: 0
                    }}
                    image={product.image}
                    alt={product.nameFr}
                  />
                  <CardContent sx={{ 
                    flexGrow: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    p: { xs: 2, sm: 3 }
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ 
                        fontSize: { xs: '1rem', sm: '1.1rem' },
                        lineHeight: 1.2,
                        flex: 1,
                        mr: 1
                      }}>
                        {product.nameFr}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => toggleFavorite({
                          id: product.id,
                          name: product.nameFr,
                          price: product.price,
                          image: product.image,
                          category: product.category,
                          rating: product.rating,
                          reviews: product.reviews,
                          isNew: product.isNew,
                          isFeatured: product.isFeatured,
                          stock: product.stock,
                        })}
                        sx={{ flexShrink: 0 }}
                      >
                        {isFavorite(product.id) ? (
                          <FavoriteIcon color="error" fontSize="small" />
                        ) : (
                          <FavoriteBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Rating value={product.rating} precision={0.1} size="small" readOnly />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({product.reviews})
                      </Typography>
                    </Box>
                    
                    <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                      {formatPrice(product.price)}
                    </Typography>
                    
                    {viewMode === 'list' && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flex: 1
                        }}
                      >
                        {product.descriptionFr}
                      </Typography>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      {product.isNew && (
                        <Chip label={t.common.new} color="secondary" size="small" />
                      )}
                      {product.isFeatured && (
                        <Chip label={t.common.featured} color="primary" size="small" />
                      )}
                      {product.stock < 50 && (
                        <Chip label={t.common.lowStock} color="warning" size="small" />
                      )}
                    </Box>
                    
                    <Box sx={{ mt: 'auto', display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        startIcon={<CartIcon />}
                        onClick={() => handleAddToCart(product)}
                        disabled={isInCart(product.id) || product.stock === 0}
                        size="small"
                        sx={{ flex: viewMode === 'list' ? 1 : 'none' }}
                      >
                        {isInCart(product.id) ? 'Dans le panier' : t.common.addToCart}
                      </Button>
                      {viewMode === 'list' && (
                        <Button
                          variant="outlined"
                          size="small"
                          component={RouterLink}
                          to={`/product/${product.id}`}
                        >
                          Voir détails
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Products; 